import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  Modal,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { cryptoInfoService } from '../../../../services/cryptoInfo';
import { vaultService } from '../../../../services/vaultService';

import { createLogger } from '../../../../utils/logger';
import { ensureCredentialWriteKey } from '../../../../services/encryption/credentialFields';
const logger = createLogger('CryptoInfoInput');

type RouteParams = {
  CryptoInfoInput: {
    cryptoId?: string;
    isEdit?: boolean;
  };
};

const WALLET_TYPES = ['Exchange', 'Hardware', 'Software', 'Paper'];
const CRYPTO_TYPES = ['Bitcoin', 'Ethereum', 'Litecoin', 'Ripple', 'Cardano', 'Polkadot', 'Chainlink', 'Stellar', 'Other'];
const DOCUMENT_LOCATIONS = [
  'Safe at Home',
  'Bank Safety Deposit Box',
  'Filing Cabinet',
  'Digital Cloud Storage',
  'With Attorney',
  'Office Safe',
  'Other Secure Location'
];

export const CryptoInfoInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<RouteParams, 'CryptoInfoInput'>>();
  const isEdit = route.params?.isEdit || false;
  const cryptoId = route.params?.cryptoId;

  const [hasOnlinePortal, setHasOnlinePortal] = useState(false);
  const [showUsername, setShowUsername] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [showSeedPhrase, setShowSeedPhrase] = useState(false);
  const [showWalletTypeModal, setShowWalletTypeModal] = useState(false);
  const [showCryptoTypeModal, setShowCryptoTypeModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showAddAnotherDone, setShowAddAnotherDone] = useState(false);
  const [savedCryptoId, setSavedCryptoId] = useState<string | undefined>(undefined);

  const [formData, setFormData] = useState({
    exchangeName: '',
    walletType: '',
    cryptocurrencyType: '',
    walletAddress: '',
    privateKey: '',
    seedPhrase: '',
    approximateValue: '',
    purchaseDate: '',
    purchasePrice: '',
    storageLocation: '',
    backupLocation: '',
    notes: '',
    username: '',
    password: '',
  });

  useEffect(() => {
    if (isEdit && cryptoId) {
      (async () => {
        try {
          const data = await cryptoInfoService.getCryptoInfoById(cryptoId);
          if (data) {
            setFormData({
              exchangeName: data.exchangeName || '',
              walletType: data.walletType || '',
              cryptocurrencyType: data.cryptocurrencyType || '',
              walletAddress: data.walletAddress || '',
              privateKey: '',
              seedPhrase: '',
              approximateValue: data.approximateValue ? formatCurrency(data.approximateValue.toString()) : '',
              purchaseDate: data.purchaseDate ? formatDate(data.purchaseDate) : '',
              purchasePrice: data.purchasePrice ? formatCurrency(data.purchasePrice.toString()) : '',
              storageLocation: data.storageLocation || '',
              backupLocation: data.backupLocation || '',
              notes: data.notes || '',
              username: '',
              password: '',
            });

            // Load sensitive data from vault
            try {
              const creds = await vaultService.getPortalCredentials(cryptoId, 'crypto');
              if (creds) {
                if (creds.username || creds.password) {
                  setHasOnlinePortal(true);
                }
                setFormData(prev => ({
                  ...prev,
                  username: creds.username || '',
                  password: creds.password || '',
                  privateKey: creds.privateKey || '',
                  seedPhrase: creds.seedPhrase || '',
                }));
              }
            } catch {}
          }
        } catch (e) {
          logger.debug('Failed to load crypto for edit', e);
        }
      })();
    }
  }, [isEdit, cryptoId]);

  const formatCurrency = (value: string) => {
    const cleaned = value.replace(/[^0-9.]/g, '');
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      parts.splice(2);
    }
    if (parts[1] && parts[1].length > 2) {
      parts[1] = parts[1].substring(0, 2);
    }
    let formatted = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (parts[1] !== undefined) {
      formatted += '.' + parts[1];
    }
    return formatted ? '$' + formatted : '';
  };

  const handleCurrencyChange = (field: 'approximateValue' | 'purchasePrice', text: string) => {
    if (text === '') {
      setFormData({ ...formData, [field]: '' });
      return;
    }
    const formatted = formatCurrency(text);
    setFormData({ ...formData, [field]: formatted });
  };

  const formatDate = (text: string) => {
    const cleaned = text.replace(/\D/g, '');
    if (cleaned.length <= 2) {
      return cleaned;
    } else if (cleaned.length <= 4) {
      return `${cleaned.slice(0, 2)}-${cleaned.slice(2)}`;
    } else {
      return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 4)}-${cleaned.slice(4, 8)}`;
    }
  };

  const dateToISO = (dateStr: string) => {
    if (!dateStr || dateStr.length !== 10) return null;
    const [day, month, year] = dateStr.split('-');
    if (!day || !month || !year || year.length !== 4) return null;
    return `${year}-${month}-${day}`;
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      // K5: a login is saved only under a Recovery Code; ask before anything is written.
      if ((formData.username || formData.password || formData.privateKey || formData.seedPhrase) && !(await ensureCredentialWriteKey())) {
        setSubmitting(false);
        return;
      }

      const payload: any = {
        exchangeName: formData.exchangeName || undefined,
        walletType: formData.walletType || undefined,
        cryptocurrencyType: formData.cryptocurrencyType || undefined,
        walletAddress: formData.walletAddress || undefined,
        approximateValue: formData.approximateValue ? parseFloat(formData.approximateValue.replace(/[$,]/g, '')) : undefined,
        purchaseDate: dateToISO(formData.purchaseDate) || undefined,
        purchasePrice: formData.purchasePrice ? parseFloat(formData.purchasePrice.replace(/[$,]/g, '')) : undefined,
        storageLocation: formData.storageLocation || undefined,
        backupLocation: formData.backupLocation || undefined,
        notes: formData.notes || undefined,
      };

      // Create or update crypto record
      let savedId = cryptoId as string | undefined;
      if (isEdit && cryptoId) {
        const updated = await cryptoInfoService.updateCryptoInfo(cryptoId, payload);
        // Handle wrapped response format { success, data, message }
        const updatedData = updated?.data || updated;
        savedId = updatedData.id;
      } else {
        const saved = await cryptoInfoService.createCryptoInfo(payload);
        // Handle wrapped response format { success, data, message }
        const createdData = saved?.data || saved;
        savedId = createdData.id;
      }

      // Store the saved ID for the Done button
      setSavedCryptoId(savedId);

      // Store sensitive data in Vault if provided
      if (savedId && (formData.username || formData.password || formData.privateKey || formData.seedPhrase)) {
        try {
          await vaultService.storePortalCredentials(savedId, 'crypto', {
            username: formData.username || undefined,
            password: formData.password || undefined,
            privateKey: formData.privateKey || undefined,
            seedPhrase: formData.seedPhrase || undefined,
          });
        } catch (e) {
          logger.error('[Portal Debug] Vault write failed for crypto credentials:', e);
        }
      }

      setShowAddAnotherDone(true);
    } catch (error: any) {
      logger.error('Error saving crypto info:', error);
      Alert.alert('Error', 'Failed to save crypto information. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWalletTypeSelect = (walletType: string) => {
    setFormData({ ...formData, walletType: walletType });
    setShowWalletTypeModal(false);
  };

  const handleCryptoTypeSelect = (cryptoType: string) => {
    setFormData({ ...formData, cryptocurrencyType: cryptoType });
    setShowCryptoTypeModal(false);
  };

  const handleLocationSelect = (location: string) => {
    setFormData({ ...formData, storageLocation: location });
    setShowLocationModal(false);
  };

  const handleAddAnother = () => {
    // Reset form and hide buttons
    setFormData({
      exchangeName: '',
      walletType: '',
      cryptocurrencyType: '',
      walletAddress: '',
      privateKey: '',
      seedPhrase: '',
      approximateValue: '',
      purchaseDate: '',
      purchasePrice: '',
      storageLocation: '',
      backupLocation: '',
      notes: '',
      username: '',
      password: '',
    });
    setHasOnlinePortal(false);
    setShowUsername(false);
    setShowPassword(false);
    setShowPrivateKey(false);
    setShowSeedPhrase(false);
    setShowAddAnotherDone(false);
    setSavedCryptoId(undefined);
  };

  const handleDone = () => {
    setSubmitting(false);
    // Navigate to lock/unlock with the saved ID
    if (savedCryptoId) {
      (navigation as any).navigate('CryptoInfoLockUnlock', { cryptoId: savedCryptoId });
    } else if (cryptoId && isEdit) {
      (navigation as any).navigate('FinanceChecklist');
    } else {
      Alert.alert('Error', 'Failed to save crypto information');
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>{isEdit ? 'Edit Crypto Info' : 'Add Crypto Currency Info'}</Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" automaticallyAdjustKeyboardInsets={true}>
          {/* Cryptocurrency Type Dropdown */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Cryptocurrency Type</Text>
            <TouchableOpacity
              style={[styles.input, { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }]}
              onPress={() => setShowCryptoTypeModal(true)}
            >
              <Text style={[
                { fontSize: 16, color: '#1C2C4F' },
                !formData.cryptocurrencyType && { color: '#A9A9A9' }
              ]}>
                {formData.cryptocurrencyType || 'Select cryptocurrency type'}
              </Text>
              <Image
                source={require('../../../../assets/icons/down.png')}
                style={[styles.dropdownIcon, { tintColor: '#1C2C4F' }]}
              />
            </TouchableOpacity>
          </View>

          {/* Wallet Type Dropdown */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Wallet Type</Text>
            <TouchableOpacity
              style={[styles.input, { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }]}
              onPress={() => setShowWalletTypeModal(true)}
            >
              <Text style={[
                { fontSize: 16, color: '#1C2C4F' },
                !formData.walletType && { color: '#A9A9A9' }
              ]}>
                {formData.walletType || 'Select wallet type'}
              </Text>
              <Image
                source={require('../../../../assets/icons/down.png')}
                style={[styles.dropdownIcon, { tintColor: '#1C2C4F' }]}
              />
            </TouchableOpacity>
          </View>

          {/* Exchange Name */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Exchange/Wallet Name</Text>
            <TextInput
              style={styles.input}
              value={formData.exchangeName}
              onChangeText={(text) => setFormData({ ...formData, exchangeName: text })}
              placeholder="Ex: Coinbase, Ledger, MetaMask"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          {/* Wallet Address */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Wallet Address</Text>
            <TextInput
              style={styles.input}
              value={formData.walletAddress}
              onChangeText={(text) => setFormData({ ...formData, walletAddress: text })}
              placeholder="Ex: 1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          {/* Approximate Value */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Approximate Value</Text>
            <TextInput
              style={styles.input}
              value={formData.approximateValue}
              onChangeText={(text) => handleCurrencyChange('approximateValue', text)}
              placeholder="$0.00"
              placeholderTextColor="#A9A9A9"
              keyboardType="numeric"
            />
          </View>

          {/* Purchase Date */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Purchase Date</Text>
            <TextInput
              style={styles.input}
              value={formData.purchaseDate}
              onChangeText={(text) => setFormData({ ...formData, purchaseDate: formatDate(text) })}
              placeholder="DD-MM-YYYY"
              placeholderTextColor="#A9A9A9"
              keyboardType="numeric"
              maxLength={10}
            />
          </View>

          {/* Purchase Price */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Purchase Price</Text>
            <TextInput
              style={styles.input}
              value={formData.purchasePrice}
              onChangeText={(text) => handleCurrencyChange('purchasePrice', text)}
              placeholder="$0.00"
              placeholderTextColor="#A9A9A9"
              keyboardType="numeric"
            />
          </View>

          {/* Toggle for Online Portal */}
          <View style={styles.inputGroup}>
            <View style={styles.toggleContainer}>
              <Text style={styles.toggleLabel}>Does your exchange have an online portal?</Text>
              <View style={styles.toggleWrapper}>
                <TouchableOpacity
                  style={[styles.toggleButton, hasOnlinePortal && styles.toggleButtonActive]}
                  onPress={() => setHasOnlinePortal(!hasOnlinePortal)}
                >
                  <View style={[styles.toggleCircle, hasOnlinePortal && styles.toggleCircleActive]} />
                </TouchableOpacity>
                <Text style={styles.toggleText}>{hasOnlinePortal ? 'Yes' : 'No'}</Text>
              </View>
            </View>
          </View>

          {hasOnlinePortal && (
            <>
              {/* Username */}
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Username 🔒</Text>
                <View style={styles.inputWithIcon}>
                  <TextInput
                    style={[styles.input, styles.inputWithEye]}
                    value={formData.username}
                    onChangeText={(text) => setFormData({ ...formData, username: text })}
                    placeholder="Enter Username"
                    placeholderTextColor="#A9A9A9"
                    autoCapitalize="none"
                    autoCorrect={false}
                    secureTextEntry={!showUsername}
                  />
                  <TouchableOpacity
                    style={styles.eyeIcon}
                    onPress={() => setShowUsername(!showUsername)}
                  >
                    <Image
                      source={showUsername ? 
                        require('../../../../assets/icons/Eye.png') : 
                        require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIconImage}
                      resizeMode="contain"
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>

              {/* Password */}
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Password 🔒</Text>
                <View style={styles.inputWithIcon}>
                  <TextInput
                    style={[styles.input, styles.inputWithEye]}
                    value={formData.password}
                    onChangeText={(text) => setFormData({ ...formData, password: text })}
                    placeholder="Enter Password"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showPassword}
                    autoCapitalize="none"
                    autoCorrect={false}
                    textContentType="password"
                  />
                  <TouchableOpacity
                    style={styles.eyeIcon}
                    onPress={() => setShowPassword(!showPassword)}
                  >
                    <Image
                      source={showPassword ? 
                        require('../../../../assets/icons/Eye.png') : 
                        require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIconImage}
                      resizeMode="contain"
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>
            </>
          )}

          {/* Private Key (Sensitive) */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Private Key 🔒 (Optional - Highly Sensitive)</Text>
            <View style={styles.inputWithIcon}>
              <TextInput
                style={[styles.input, styles.inputWithEye, showPrivateKey && styles.textArea]}
                value={formData.privateKey}
                onChangeText={(text) => setFormData({ ...formData, privateKey: text })}
                placeholder="Enter private key"
                placeholderTextColor="#A9A9A9"
                secureTextEntry={!showPrivateKey}
                multiline={showPrivateKey}
                textAlignVertical={showPrivateKey ? 'top' : 'center'}
              />
              <TouchableOpacity
                style={[styles.eyeIcon, showPrivateKey && { top: 15 }]}
                onPress={() => setShowPrivateKey(!showPrivateKey)}
              >
                <Image
                  source={showPrivateKey ? 
                    require('../../../../assets/icons/Eye.png') : 
                    require('../../../../assets/icons/Eye-closed.png')}
                  style={styles.eyeIconImage}
                  resizeMode="contain"
                />
              </TouchableOpacity>
            </View>
            <Text style={[styles.vaultText, { color: '#DC3545' }]}>⚠️ Extremely sensitive - will be encrypted in vault</Text>
          </View>

          {/* Seed Phrase (Sensitive) */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Seed Phrase 🔒 (Optional - Highly Sensitive)</Text>
            <View style={styles.inputWithIcon}>
              <TextInput
                style={[styles.input, styles.inputWithEye, showSeedPhrase && styles.textArea]}
                value={formData.seedPhrase}
                onChangeText={(text) => setFormData({ ...formData, seedPhrase: text })}
                placeholder="Enter seed phrase"
                placeholderTextColor="#A9A9A9"
                secureTextEntry={!showSeedPhrase}
                multiline={showSeedPhrase}
                textAlignVertical={showSeedPhrase ? 'top' : 'center'}
              />
              <TouchableOpacity
                style={[styles.eyeIcon, showSeedPhrase && { top: 15 }]}
                onPress={() => setShowSeedPhrase(!showSeedPhrase)}
              >
                <Image
                  source={showSeedPhrase ? 
                    require('../../../../assets/icons/Eye.png') : 
                    require('../../../../assets/icons/Eye-closed.png')}
                  style={styles.eyeIconImage}
                  resizeMode="contain"
                />
              </TouchableOpacity>
            </View>
            <Text style={[styles.vaultText, { color: '#DC3545' }]}>⚠️ Extremely sensitive - will be encrypted in vault</Text>
          </View>

          {/* Storage Location */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Where do you keep your wallet/keys?</Text>
            <TouchableOpacity
              style={[styles.input, { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }]}
              onPress={() => setShowLocationModal(true)}
            >
              <Text style={[
                { fontSize: 16, color: '#1C2C4F' },
                !formData.storageLocation && { color: '#A9A9A9' }
              ]}>
                {formData.storageLocation || 'Select Storage Location'}
              </Text>
              <Image
                source={require('../../../../assets/icons/down.png')}
                style={[styles.dropdownIcon, { tintColor: '#1C2C4F' }]}
              />
            </TouchableOpacity>
          </View>

          {/* Backup Location */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Backup Location</Text>
            <TextInput
              style={styles.input}
              value={formData.backupLocation}
              onChangeText={(text) => setFormData({ ...formData, backupLocation: text })}
              placeholder="Ex: Safety deposit box, Cloud backup"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          {/* Notes */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Notes</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.notes}
              onChangeText={(text) => setFormData({ ...formData, notes: text })}
              placeholder="Additional notes..."
              placeholderTextColor="#A9A9A9"
              multiline={true}
              textAlignVertical="top"
            />
          </View>

          {!showAddAnotherDone ? (
            <View style={styles.buttonContainer}>
              <TouchableOpacity
                style={[styles.submitButton]}
                onPress={handleSubmit}
                disabled={submitting}
              >
                {submitting ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <Text style={styles.submitButtonText}>{isEdit ? 'Update' : 'Submit'}</Text>
                )}
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.addAnotherDoneContainer}>
              <TouchableOpacity
                style={[styles.submitButton, styles.addAnotherButton]}
                onPress={handleAddAnother}
              >
                <Text style={styles.submitButtonText}>Add Another</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.submitButton, styles.doneButton]}
                onPress={handleDone}
              >
                <Text style={styles.submitButtonText}>Done</Text>
              </TouchableOpacity>
            </View>
          )}

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      {/* Wallet Type Modal */}
      <Modal
        visible={showWalletTypeModal}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowWalletTypeModal(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowWalletTypeModal(false)}
        >
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Wallet Type</Text>
              <TouchableOpacity
                style={styles.modalCloseButton}
                onPress={() => setShowWalletTypeModal(false)}
              >
                <Text style={styles.modalCloseText}>×</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.stateList}>
              {WALLET_TYPES.map((type) => (
                <TouchableOpacity
                  key={type}
                  style={styles.stateItem}
                  onPress={() => handleWalletTypeSelect(type)}
                >
                  <Text style={styles.stateItemText}>{type}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Crypto Type Modal */}
      <Modal
        visible={showCryptoTypeModal}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowCryptoTypeModal(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowCryptoTypeModal(false)}
        >
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Cryptocurrency</Text>
              <TouchableOpacity
                style={styles.modalCloseButton}
                onPress={() => setShowCryptoTypeModal(false)}
              >
                <Text style={styles.modalCloseText}>×</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.stateList}>
              {CRYPTO_TYPES.map((type) => (
                <TouchableOpacity
                  key={type}
                  style={styles.stateItem}
                  onPress={() => handleCryptoTypeSelect(type)}
                >
                  <Text style={styles.stateItemText}>{type}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Storage Location Modal */}
      <Modal
        visible={showLocationModal}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowLocationModal(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowLocationModal(false)}
        >
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Storage Location</Text>
              <TouchableOpacity
                style={styles.modalCloseButton}
                onPress={() => setShowLocationModal(false)}
              >
                <Text style={styles.modalCloseText}>×</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.stateList}>
              {DOCUMENT_LOCATIONS.map((location) => (
                <TouchableOpacity
                  key={location}
                  style={styles.stateItem}
                  onPress={() => handleLocationSelect(location)}
                >
                  <Text style={styles.stateItemText}>{location}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(120),
    height: s(120),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  vaultText: {
    fontSize: ms(12, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    fontStyle: 'italic',
    marginLeft: ms(2),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  inputWithIcon: {
    position: 'relative',
  },
  inputWithEye: {
    paddingRight: ms(50),
  },
  eyeIcon: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -10 }],
    padding: ms(5),
    zIndex: 1,
  },
  eyeIconImage: {
    width: s(20),
    height: s(20),
    tintColor: '#6B7280',
  },
  textArea: {
    height: s(100),
    textAlignVertical: 'top',
  },
  buttonContainer: {
    paddingVertical: ms(20),
    paddingHorizontal: ms(16),
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  bottomPadding: {
    height: s(120),
  },
  addAnotherDoneContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: ms(20),
    paddingHorizontal: ms(16),
    gap: ms(15),
  },
  addAnotherButton: {
    backgroundColor: '#FF9F64',
    flex: 1,
  },
  doneButton: {
    backgroundColor: '#29BFC0',
    flex: 1,
  },
  dropdownIcon: {
    width: s(20),
    height: s(20),
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    maxHeight: '80%',
    width: '100%',
    paddingBottom: ms(20),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: ms(16),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  modalTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  modalCloseButton: {
    padding: ms(8),
  },
  modalCloseText: {
    fontSize: ms(20, 0.3),
    color: '#1C2C4F',
  },
  stateList: {
    padding: ms(16),
  },
  stateItem: {
    paddingVertical: ms(12),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  stateItemText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  toggleContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: ms(8),
  },
  toggleLabel: {
    fontSize: ms(16, 0.3),
    fontWeight: '500',
    color: '#1C2C4F',
    flex: 1,
  },
  toggleWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: ms(12),
  },
  toggleButton: {
    width: s(60),
    height: s(32),
    backgroundColor: '#E5E7EB',
    borderRadius: ms(16),
    padding: ms(2),
    justifyContent: 'center',
  },
  toggleButtonActive: {
    backgroundColor: '#FB923C',
  },
  toggleCircle: {
    width: s(28),
    height: s(28),
    backgroundColor: '#FFFFFF',
    borderRadius: ms(14),
    alignSelf: 'flex-start',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  toggleCircleActive: {
    alignSelf: 'flex-end',
  },
  toggleText: {
    fontSize: ms(16, 0.3),
    fontWeight: '500',
    color: '#1C2C4F',
    minWidth: s(30),
  },
});